<template>
    <div ref="automation-editor"
         class="automation-editor"
         :class="{'text-left': changeable}">
        <AutomationEditorTrigger
            @change="onTriggerChange"
            :trigger="trigger"
            :type="type"
            :changeable="changeable">
            <template v-if="changeable && trigger.code" v-slot:after-trigger>
                <AutomationConditionAddBtn
                    v-if="conditions.length === 0"
                    :conditions="availableConditions"
                    @add="onConditionAdd"
                    class="plus-btn"/>
                <AutomationEditorSentencePartDeleteBtn
                    @delete="onTriggerDelete"
                    :disabled="!changeable"/>
                <AppSwitch v-if="showChangeableSwitch"
                           :value="trigger.changeable"
                           :disabled="!changeable"
                           @toggle="onTriggerToggle"
                           class="toggle-btn">
                    <div slot="label">Changeable</div>
                </AppSwitch>
            </template>
        </AutomationEditorTrigger>

        <AutomationConditionsEditor
            @change="onConditionsChange"
            :conditions="conditions"
            :changeable="changeable">
            <template #before-condition>
                <span v-if="conditions.length !== 0">
                    {{ trigger.code === AutomationTriggerCodes.SCHEDULE ? 'when' : 'and' }}
                </span>
            </template>
            <template #after-condition="{idx}">
                <template v-if="changeable && conditions[idx]">
                    <AutomationConditionAddBtn
                        v-if="idx === conditions.length - 1"
                        :conditions="availableConditions"
                        @add="onConditionAdd"
                        class="plus-btn"/>
                    <AutomationEditorSentencePartDeleteBtn
                        @delete="onConditionDelete(idx)"
                        :disabled="!isTemplateEditMode && !_.get(conditions[idx], 'changeable')"/>
                </template>
                <AppSwitch v-if="showChangeableSwitch"
                           :value="conditions[idx].changeable"
                           :disabled="!changeable"
                           @toggle="onConditionToggle(idx)"
                           class="toggle-btn">
                    <div slot="label">Changeable</div>
                </AppSwitch>
            </template>
        </AutomationConditionsEditor>

        <AutomationEditorActions
            @change="onActionsChange"
            :type="type"
            :actions="actions"
            :changeable="changeable">
            <template v-slot:prefix>
                <div v-if="changeable" class="arrow-down-to-actions">
                    <i class="fal fa-arrow-down"></i>
                </div>
            </template>
            <template #after-action="{idx}">
                <template v-if="changeable">
                    <div v-if="actions.length !== 0 && idx === actions.length -1 && actions[idx]"
                         @click="onActionAdd"
                         class="plus-btn">
                        <i class="fal fa-plus"></i>
                    </div>
                    <AutomationEditorSentencePartDeleteBtn
                        @delete="onActionDelete(idx)"
                        :disabled="!isTemplateEditMode && !!actions[idx] && !_.get(actions[idx], 'changeable')"/>
                </template>
                <AppSwitch v-if="showChangeableSwitch && !!actions[idx]"
                           :value="actions[idx].changeable"
                           :disabled="!changeable"
                           @toggle="onActionToggle(idx)"
                           class="toggle-btn">
                    <div slot="label">Changeable</div>
                </AppSwitch>
            </template>
        </AutomationEditorActions>
    </div>
</template>

<script>
import {AutomationTriggerCodes} from '@/types/automations'
import AutomationEditorTrigger from './trigger/AutomationEditorTrigger'
import AutomationConditionsEditor from './conditions/AutomationConditionsEditor'
import AutomationEditorActions from './actions/AutomationEditorActions'
import AutomationConditionAddBtn from './conditions/AutomationConditionAddBtn'
import AppSwitch from '../shared/AppSwitch'
import AutomationEditorSentencePartDeleteBtn from './sentence/AutomationEditorSentencePartDeleteBtn'
import {AutomationTypes} from '@/types/automations/types'

export default {
    name: 'AutomationEditor',
    inject: ['getAvailableConditions'],
    components: {
        AppSwitch,
        AutomationEditorActions,
        AutomationConditionsEditor,
        AutomationEditorTrigger,
        AutomationEditorSentencePartDeleteBtn,
        AutomationConditionAddBtn
    },
    props: {
        type: {
            type: String,
            required: true,
            default: AutomationTypes.AUTOMATION
        },
        recipe: { // AutomationRecipe
            type: Object,
            required: true
        },
        isTemplateEditMode: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            AutomationTriggerCodes
        }
    },
    computed: {
        trigger() { // AutomationTrigger
            return _.isPlainObject(this.recipe.trigger) ? this.recipe.trigger : {}
        },

        // optional recipe part
        conditions() { // Array<AutomationCondition>
            return _.isArray(this.recipe.conditions) ? this.recipe.conditions : []
        },

        actions() { // Array<AutomationAction>
            return _.isArray(this.recipe.actions) ? this.recipe.actions : []
        },

        isMarketplace() {
            return this.type === AutomationTypes.MARKETPLACE
        },

        isChangeableRecipe() {
            return !this.recipe.hasOwnProperty('changeable') || !!this.recipe.changeable
        },

        changeable() {
            return this.isChangeableRecipe || this.isTemplateEditMode
        },

        showChangeableSwitch() {
            return this.isTemplateEditMode && !this.isMarketplace
        },

        availableConditions() {
            return this.getAvailableConditions({type: this.type})
        }
    },
    methods: {
        onChange({path, data}) {
            this.$emit('change', {path, data})
        },

        onTriggerChange({path, data}) {
            this.onChange({path: ['trigger'].concat(path), data})
            if (!this.isMarketplace && !this.trigger.hasOwnProperty('changeable')) {
                this.onChange({path: ['trigger', 'changeable'], data: true})
            }
        },

        onTriggerDelete() {
            this.onChange({path: ['trigger'], data: null})
        },

        onConditionsChange({path, data}) {
            this.onChange({path: ['conditions'].concat(path), data})
        },

        onActionsChange({path, data}) {
            this.onChange({path: ['actions'].concat(path), data})
        },

        onTriggerToggle() {
            this.onChange({path: ['trigger', 'changeable'], data: !this.trigger.changeable})
        },

        onConditionToggle(idx) {
            this.onChange({
                path: ['conditions', idx, 'changeable'],
                data: !this.conditions[idx].changeable
            })
        },

        onActionToggle(idx) {
            this.onChange({
                path: ['actions', idx, 'changeable'],
                data: !this.conditions[idx].changeable
            })
        },

        onConditionDelete(idx) {
            const conditions = _.cloneDeep(this.conditions)
            conditions.splice(idx, 1)
            this.onChange({path: ['conditions'], data: conditions})
        },

        onConditionAdd(condition) {
            const conditions = _.cloneDeep(this.conditions)
            condition.changeable = !this.isMarketplace
            conditions.push(condition)
            this.onChange({path: ['conditions'], data: conditions})
        },

        onActionDelete(idx) {
            const actions = _.cloneDeep(this.actions)
            actions.splice(idx, 1)
            this.onChange({path: ['actions'], data: actions})
        },

        onActionAdd() {
            const actions = _.cloneDeep(this.actions)
            actions.push(null)
            this.onChange({path: ['actions'], data: actions})
        }
    }
}
</script>

<style scoped lang="scss">
@import './index.scss';

.delete-btn,
.plus-btn,
.toggle-btn {
    position: absolute;
    top: 4px;
    font-size: 20px;

    &:hover {
        cursor: pointer;
    }

    &.disabled {
        color: $color-grey-light
    }
}

.delete-btn {
    right: -90px;
}

.plus-btn {
    right: -40px;
}

.toggle-btn {
    top: 12px;
    right: -250px;
    font-size: 18px;
}

.arrow-down-to-actions {
    text-align: left;
    font-size: 24px;
    margin: 16px 0;
    color: $color-purple-primary;
    font-weight: bold;
}
</style>
